import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { TabViewChangeEvent } from 'primeng/tabview';
import { TabId } from 'src/app/enums/tab-id.enum';
import { EntityType } from 'src/app/enums/entity-type.enum';
import { AuthorizedCounts, Entity, UnauthorizedLicense } from 'src/app/models/mvp-query-response-dto';
import { RoutingService } from 'src/app/services/routing.service';
import { SearchService } from 'src/app/services/search.service';
import { DEFAULT_ENTITY_INDEX, ENTITY_TYPE_SHORT, RESULTS_TABLE_COLUMNS } from 'src/app/constants';
import { pluralToSingular } from 'src/app/enums/entity-type.enum';
import { combineLatest, Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { ResultsBodyTableComponent } from './results-body/results-body-table/results-body-table.component';
import { Table } from 'primeng/table';
import { MultiLangStrDto } from 'src/app/models/multi-lang-str-dto';
import { LanguageService } from 'src/app/services/language.service';
import { GaService } from 'src/app/services/ga.service';


interface Column {
  field: string;
  filterField?: string;
  header: MultiLangStrDto;
  styleClass?: string;
  sortable?: boolean;
  filterable?: boolean;
}

@Component({
  selector: 'app-results',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss']
})
export class ResultsComponent implements OnInit, OnDestroy {
  @ViewChild('rbt') resultsBodyTable!: ResultsBodyTableComponent;
  @ViewChild('dt', { static: true }) table?: Table;
  @ViewChild('tabView') tabView: any;
  @ViewChild('filterBtn', { read: ElementRef }) filterBtnRef?: ElementRef;
  @Input() filterFromMap: boolean = false;

  TabId = TabId;
  activeTabIndex: number = TabId.DEFAULT;
  entityIndex: number = DEFAULT_ENTITY_INDEX;
  isLoading = false;
  isLoadingMoreResults = false;
  isErrorTooManyRows = false;
  isErrorNoPolygons = false;
  withImagesOnly = false;

  entities: Entity[] = [];
  filteredEntities: Entity[] = [];
  authorizedCounts: AuthorizedCounts = {} as AuthorizedCounts;
  unauthorizedCounts: AuthorizedCounts = {} as AuthorizedCounts;
  numberOfAllEntitiesUnauthorized = 0;
  unauthorizedLicenses: Array<{ license: string; expedition: string }> = [];

  currentLanguage!: string;
  isFirstSearchTriggered = false;

  isUnauthorizedDialogOpen = false;
  isFilterDialogOpen = false;

  // --- Feature Flag to easily revert changes ---
  // Set to true to revert back to the original filter button, toggle, and tag behavior
  readonly useOriginalFilterBehavior = false;

  // --- Filter state ---
  // Whether the filter is active (toggle on = filter applied, toggle off = show all)
  filterEnabled = true;
  // The user's current type selection (maintained even when toggle is off)
  selectedTypes: string[] = [];
  // The "default" selection for the current search (defaultOffTypes-filtered, or explicit query types)
  private defaultTypes: string[] = [];
  // Tag multi-select narrowing (within the currently displayed entities)
  activatedTags: string[] = [];
  // Working copy inside the filter dialog
  dialogSelectedTypes: string[] = [];

  cols: Column[] = RESULTS_TABLE_COLUMNS;

  private readonly defaultOffTypes: EntityType[] = [
    EntityType.SURVEY,
    EntityType.EXPEDITION,
    EntityType.LICENSE,
    EntityType.LOCUS,
    EntityType.BASKET,
    EntityType.DANA_PDF,
    EntityType.YAZAM_REPORT,
  ];

  readonly entityTypeDisplayOrder: string[] = [
    EntityType.HACHRAZA,
    EntityType.EXCAVATION,
    EntityType.OBJECT,
    EntityType.COIN,
    EntityType.MANUSCRIPT,
    EntityType.PUBLICATION,
    EntityType.SCIENTIFIC_ARCHIVE_DOCUMENTS,
    EntityType.CONSERVATION_ARCHIVE_DOCUMENTS,
    EntityType.PHOTOGRAMMETRIC,
    EntityType.ORTHOPHOTO,
    EntityType.CONSERVATION_REPORT,
    EntityType.PLASTER_SAMPLE,
    EntityType.HB_SECTION,
    'decision_letters',
    EntityType.PERSON,
    EntityType.SURVEY,
    EntityType.EXPEDITION,
    EntityType.LICENSE,
    EntityType.LOCUS,
    EntityType.BASKET,
    EntityType.DANA_PDF,
    EntityType.YAZAM_REPORT,
  ];

  private languageSubscription?: Subscription;
  private isInitialized = false;

  private tabEntityMapping: { [key: number]: string } = {
    [TabId.TABLE]: 'table',
    [TabId.LIST]: 'list',
    [TabId.MAP]: 'map',
  };

  // ---- Computed getters ----

  /** Total authorized entities across all returned types. */
  get totalAuthorizedCount(): number {
    return Object.values(this.authorizedCounts).reduce((a, b) => a + b, 0);
  }

  /**
   * Count of entities covered by the current selection.
   * Used in the "All" tag label when filter is ON.
   */
  get selectedFilteredCount(): number {
    return this.selectedTypes.reduce((sum, type) => sum + (this.authorizedCounts[type] || 0), 0);
  }

  /** How many of the available (non-zero) types are currently selected — for the filter badge. */
  get selectedAvailableCount(): number {
    return this.selectedTypes.filter(t => !!this.authorizedCounts[t]).length;
  }

  /** Total number of available (non-zero) types — denominator for the filter badge. */
  get totalAvailableCount(): number {
    return Object.keys(this.authorizedCounts).length;
  }

  /** Whether no specific tag is activated (i.e. "All" tag is active). */
  get isAllTagActive(): boolean {
    if (this.useOriginalFilterBehavior) {
      return this.activatedTags.length === 0;
    }
    return this.selectedTypes.length === 0;
  }

  /**
   * Which entity types to show as tags in the strip.
   * Toggle OFF → all returned types.
   * Toggle ON  → only the selected types.
   */
  get visibleTagTypes(): string[] {
    if (this.useOriginalFilterBehavior) {
      if (!this.filterEnabled) {
        return Object.keys(this.authorizedCounts);
      }
      return this.selectedTypes.filter(t => !!this.authorizedCounts[t]);
    }
    return Object.keys(this.authorizedCounts);
  }

  /** Sorted dialog order (alphabetical by display name in current language). */
  get sortedDialogOrder(): string[] {
    return [...this.entityTypeDisplayOrder].sort((a, b) => {
      const getName = (key: string): string => {
        const d = ENTITY_TYPE_SHORT[key];
        if (!d) return key;
        return (this.currentLanguage === 'he' ? d.hebrew : d.english).trim();
      };
      return getName(a).localeCompare(getName(b), this.currentLanguage, { sensitivity: 'base' });
    });
  }

  /** isAllSelected is used by the map component to know if no type filter is active. */
  get isAllSelected(): boolean {
    if (this.useOriginalFilterBehavior) {
      return !this.filterEnabled || this.selectedTypes.length === 0;
    }
    return this.selectedTypes.length === 0;
  }

  // Comparator for the tag strip: alphabetical by display name in current language
  alphabeticOrder = (a: any, b: any): number => {
    const getName = (key: string): string => {
      const d = ENTITY_TYPE_SHORT[key];
      if (!d) return key;
      return (this.currentLanguage === 'he' ? d.hebrew : d.english).trim();
    };
    return getName(a.key).localeCompare(getName(b.key), this.currentLanguage, { sensitivity: 'base' });
  };

  constructor(
    public searchService: SearchService,
    public gaService: GaService,
    public routingService: RoutingService,
    private location: Location,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
  ) {
    const selectedMenuCategory = (this.location.getState() as any).page;
    if (selectedMenuCategory) {
      this.routingService.updateSelectedMenuCategory(selectedMenuCategory);
    }
  }

  ngOnInit(): void {
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(language => {
      this.currentLanguage = language;
      if (this.entities.length) {
        this.nameAndThesaurusByLanguage();
      }
    });

    this.routingService.activeTabIndex$.subscribe((tabIndex: number) => {
      this.activeTabIndex = tabIndex;
    });

    this.searchService.isErrorTooManyRows$.subscribe((v: boolean) => { this.isErrorTooManyRows = v; });
    this.searchService.isErrorNoPolygons$.subscribe((v: boolean) => { this.isErrorNoPolygons = v; });
    this.searchService.isLoadingResults$.subscribe((v: boolean) => {
      this.isLoading = v;
      this.isLoadingMoreResults = v;
    });
    this.searchService.isFirstSearchResReturned$.subscribe((v: boolean) => { this.isFirstSearchTriggered = v; });

    this.searchService.searchCleared$.subscribe(() => {
      this.routingService.updateEntityIndex(DEFAULT_ENTITY_INDEX);
      this.isInitialized = false;
    });

    combineLatest([
      this.searchService.searchResults$,
      this.searchService.authorizedCounts$,
      this.searchService.unauthorizedCounts$,
      this.searchService.unauthorizedLicenses$,
    ]).subscribe(([entitiesRes, authorizedCounts, unauthorizedCounts, unauthorizedLicenses]) => {
      this.processEntities(entitiesRes);

      // Capture previous keys to detect a real new search vs. image-toggle re-emit
      const previousCountKeys = new Set(Object.keys(this.authorizedCounts));

      this.authorizedCounts = this.filterCounts(authorizedCounts);
      this.unauthorizedCounts = this.filterCounts(unauthorizedCounts);
      this.numberOfAllEntitiesUnauthorized = Object.values(this.unauthorizedCounts).reduce((a, b) => a + b, 0);

      const availableTypes = Object.keys(this.authorizedCounts);
      // entityTypes in search params use plural form (e.g. 'licenses'); authorizedCounts keys use singular (e.g. 'license')
      const entityTypesInQuery: string[] = (this.searchService.searchFields?.entityTypes || [])
        .map(f => (pluralToSingular as Record<string, string>)[f.id] ?? f.id);

      const countsChanged =
        availableTypes.some(t => !previousCountKeys.has(t)) ||
        [...previousCountKeys].some(t => !availableTypes.includes(t));

      if (!this.isInitialized || countsChanged) {
        this.isInitialized = true;
        if (this.useOriginalFilterBehavior) {
          this.filterEnabled = true;
          this.activatedTags = [];
        } else {
          // Set filterEnabled to false so it always displays all filter tags, but default entity types are selected by default
          this.filterEnabled = false;
          this.activatedTags = [];
        }

        if (entityTypesInQuery.length > 0) {
          // Search had explicit entity types — those become the default
          this.defaultTypes = availableTypes.filter(t => entityTypesInQuery.includes(t));
        } else {
          // No explicit types — default excludes the "off" types
          this.defaultTypes = availableTypes.filter(t => !this.defaultOffTypes.includes(t as EntityType));
        }

        if (this.useOriginalFilterBehavior) {
          this.selectedTypes = [...this.defaultTypes];
        } else {
          // Default to "All" (empty selectedTypes) when no explicit entity type is specified in the search query.
          // Otherwise, select only the searched categories.
          this.selectedTypes = entityTypesInQuery.length > 0 ? [...this.defaultTypes] : [];
        }
      }

      this.applyEntityTypeFilter();
      this.processUnauthorizedLicenses(unauthorizedLicenses);
    });

    combineLatest([this.searchService.searchResults$, this.routingService.entityIndex$]).subscribe(
      ([_, entityIndex]) => { this.entityIndex = entityIndex; }
    );
  }

  // ---- Entity processing ----

  processEntities(entitiesRes: Entity[]): void {
    const entities = entitiesRes
      .filter(entity =>
        entity.entityType !== EntityType.SCIENTIFIC_ARCHIVE_FOLDERS &&
        entity.entityType !== EntityType.CONSERVATION_ARCHIVE_FOLDERS &&
        entity.entityType !== EntityType.FRAGMENT &&
        entity.entityType !== EntityType.HB_FINDING
      )
      .map(e => ({
        ...e,
        nameByLanguage: this.currentLanguage === 'he' ? e.entityTypeName.hebrew : e.entityTypeName.english,
        thesaurusString: this.getThesaurusTitles(e),
        licensesString: this.getLicenceNames(e),
      }))
      .sort((a, b) => a.entityId - b.entityId);

    this.entities = entities;
    this.filteredEntities = [...entities];
  }

  filterCounts(counts: AuthorizedCounts): AuthorizedCounts {
    const filtered: AuthorizedCounts = {};
    for (const [key, value] of Object.entries(counts)) {
      if (
        key !== EntityType.SCIENTIFIC_ARCHIVE_FOLDERS &&
        key !== EntityType.CONSERVATION_ARCHIVE_FOLDERS &&
        key !== EntityType.FRAGMENT &&
        key !== EntityType.HB_FINDING
      ) {
        filtered[key] = value;
      }
    }
    return filtered;
  }

  processUnauthorizedLicenses(unauthorizedLicenses: UnauthorizedLicense[]): void {
    this.unauthorizedLicenses = unauthorizedLicenses.map(e => ({
      license: e.license.longName,
      expedition: e.expedition.expeditionName.hebrew,
    }));
  }

  getLicenceNames(entity: Entity): string {
    return entity.licenses.map(l => l.longName).join(', ');
  }

  getThesaurusTitles(entity: Entity): MultiLangStrDto {
    const hebrewValues = entity.thesaurus
      .map((t: any) => t.categoryName?.hebrew ? `${t.categoryName.hebrew}: ${t.wordName.hebrew}` : t.wordName.hebrew)
      .join(', ');
    const englishValues = entity.thesaurus
      .map((t: any) => t.categoryName?.english ? `${t.categoryName.english}: ${t.wordName.english}` : t.wordName.english)
      .join(', ');
    entity.thesaurusByLanguage = this.currentLanguage === 'he' ? hebrewValues : englishValues;
    return new MultiLangStrDto(hebrewValues, englishValues);
  }

  nameAndThesaurusByLanguage(): void {
    this.entities = this.entities
      .map(e => ({
        ...e,
        nameByLanguage: this.currentLanguage === 'he' ? e.entityTypeName.hebrew : e.entityTypeName.english,
        thesaurusByLanguage: this.currentLanguage === 'he' ? e.thesaurusString.hebrew : e.thesaurusString.english,
      }))
      .sort((a, b) => a.entityId - b.entityId);
  }

  // ---- Core filter logic ----

  /**
   * Recomputes filteredEntities and pushes the filter to the table component.
   */
  private applyEntityTypeFilter(): void {
    let effectiveTypes: string[] | null;

    if (this.useOriginalFilterBehavior) {
      if (!this.filterEnabled && this.activatedTags.length === 0) {
        effectiveTypes = null; // show everything
      } else if (this.activatedTags.length > 0) {
        effectiveTypes = this.activatedTags;
      } else {
        effectiveTypes = this.selectedTypes.length > 0 ? this.selectedTypes : null;
      }
    } else {
      effectiveTypes = this.selectedTypes.length > 0 ? this.selectedTypes : null;
    }

    this.filteredEntities = effectiveTypes
      ? this.entities.filter(e => effectiveTypes!.includes(e.entityType))
      : [...this.entities];

    if (!effectiveTypes) {
      this.resultsBodyTable?.table?.filter(null, 'entityType', 'in');
    } else {
      this.resultsBodyTable?.triggerExternalFilter('entityType', effectiveTypes);
    }
  }

  // ---- Toggle ----

  toggleFilterEnabled(): void {
    this.applyEntityTypeFilter();
  }

  // ---- Tag strip ----

  toggleTagActivation(key: string): void {
    if (this.useOriginalFilterBehavior) {
      const idx = this.activatedTags.indexOf(key);
      if (idx > -1) {
        this.activatedTags = this.activatedTags.filter(t => t !== key);
      } else {
        this.activatedTags = [...this.activatedTags, key];
      }
    } else {
      const idx = this.selectedTypes.indexOf(key);
      if (idx > -1) {
        this.selectedTypes = this.selectedTypes.filter(t => t !== key);
      } else {
        this.selectedTypes = [...this.selectedTypes, key];
      }
    }
    this.applyEntityTypeFilter();
  }

  clearTagActivations(): void {
    if (this.useOriginalFilterBehavior) {
      this.activatedTags = [];
    } else {
      this.selectedTypes = [];
    }
    this.applyEntityTypeFilter();
  }

  // ---- Filter dialog ----

  openFilterDialog(): void {
    this.dialogSelectedTypes = [...this.selectedTypes];
    this.isFilterDialogOpen = true;
  }

  confirmFilterDialog(): void {
    // Drop activated tags that are no longer in the new selection
    this.activatedTags = this.activatedTags.filter(t => this.dialogSelectedTypes.includes(t));
    this.selectedTypes = [...this.dialogSelectedTypes];
    this.isFilterDialogOpen = false;
    this.applyEntityTypeFilter();
  }

  selectAllTypes(): void {
    const available = this.entityTypeDisplayOrder.filter(t => !!this.authorizedCounts[t]);
    const zeroResult = this.dialogSelectedTypes.filter(t => !this.authorizedCounts[t]);
    this.dialogSelectedTypes = [...new Set([...available, ...zeroResult])];
  }

  unselectAllTypes(): void {
    // Keep zero-result types (they are disabled in the dialog)
    this.dialogSelectedTypes = this.dialogSelectedTypes.filter(t => !this.authorizedCounts[t]);
  }

  restoreDefaultsInDialog(): void {
    const zeroResultSelected = this.dialogSelectedTypes.filter(t => !this.authorizedCounts[t]);
    this.dialogSelectedTypes = [...this.defaultTypes, ...zeroResultSelected];
  }

  toggleDialogType(key: string, checked: boolean): void {
    if (checked) {
      if (!this.dialogSelectedTypes.includes(key)) {
        this.dialogSelectedTypes = [...this.dialogSelectedTypes, key];
      }
    } else {
      this.dialogSelectedTypes = this.dialogSelectedTypes.filter(t => t !== key);
    }
  }

  onFilterDialogShow(): void {
    setTimeout(() => {
      const firstCheckbox = document.querySelector<HTMLElement>('.filter-dialog .p-checkbox-box');
      firstCheckbox?.focus();
    }, 50);
  }

  onFilterDialogHide(): void {
    setTimeout(() => {
      this.filterBtnRef?.nativeElement?.focus();
    }, 0);
  }

  // ---- Misc handlers ----

  handleChange(event: TabViewChangeEvent): void {
    const tab = this.tabEntityMapping[event.index];
    if (tab) {
      this.gaService.trackWithGA(tab, 'tab');
    }
    this.routingService.updateActiveTabIndex(event.index);
  }

  updateFilteredEntities(event: { entities: Entity[]; skipNavigate: boolean }): void {
    const { entities, skipNavigate } = event;
    if (entities.length > 0 || this.entities.length === 0) {
      this.filteredEntities = [...entities];
    }
    if (!skipNavigate) {
      this.routingService.updateEntityIndex(DEFAULT_ENTITY_INDEX);
    }
  }

  clearFilters(): void {
    this.resultsBodyTable.clearFilters();
    this.applyEntityTypeFilter();
    this.routingService.updateEntityIndex(DEFAULT_ENTITY_INDEX);
  }

  getEntityIndex(): number {
    return this.entityIndex < this.filteredEntities.length ? this.entityIndex : DEFAULT_ENTITY_INDEX;
  }

  getEntityTypeName(key: string): MultiLangStrDto {
    return ENTITY_TYPE_SHORT[key] || key;
  }

  handleMapClick(entities: Entity[]): void {
    this.resultsBodyTable.clearFilters();
    this.resultsBodyTable.triggerMapFilter(entities[0].entityId);
    this.routingService.navigateTable(0);
  }

  handleEntityTypesChangedFromMap(entityTypes: string[]): void {
    this.selectedTypes = entityTypes;
    this.activatedTags = [];
    this.applyEntityTypeFilter();
    this.cdr.detectChanges();
  }

  shouldDisplayClearFilter(): boolean {
    if (this.filterFromMap) return false;
    const tableFilters = this.resultsBodyTable?.table?.filters || {};
    return Object.keys(tableFilters).some(key => key !== 'entityType');
  }

  handleCloseDialog(): void {
    this.isUnauthorizedDialogOpen = false;
  }

  handleOpenDialog(): void {
    this.isUnauthorizedDialogOpen = true;
  }

  handleFilterFromMap(filterMap: boolean): void {
    this.filterFromMap = filterMap;
  }

  toggleShowOnlyWithImages(): void {
    this.searchService.toggleWithImagesOnly(this.withImagesOnly);
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }
}